import { AutomaticFontVariant, FontVariant } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { Dolan } from '@sapappgyver/appgyver-descriptors/lib/dolan';
import remove from 'lodash/remove';
import { produce } from 'immer';
import { createSelector } from '@reduxjs/toolkit';
import { defaultFontFamilies, defaultFonts } from '../../modules/fonts';
import getAllUsedFontVariantsInDolan from './getAllUsedFontVariantsInDolan';

function findFont<T extends { font_family: string; weight: string; style: string }>(
  fonts: T[] | undefined | null,
  fontFamily: string,
  fontWeight: string,
  fontStyle: string,
): T | undefined {
  return fonts?.find(
    (font) => font.font_family === fontFamily && font.weight === fontWeight && font.style === fontStyle,
  );
}
/**
 * Ensures that all default fonts that are used anywhere in the component hierarchy in
 * DOLAN are listed in DOLAN fonts listing. It will also cleanup any default fonts
 * that are no longer been used in the app (unless the default font has been manually
 * included to the DOLAN).
 *
 * This returns the new fresh list of fonts, without mutating the given DOLAN.
 */
const getAllFontsInDolan = createSelector(
  (dolan: Dolan) => dolan.fonts,
  (dolan: Dolan) => getAllUsedFontVariantsInDolan(dolan),
  (fonts, usedFontVariants): Array<FontVariant | AutomaticFontVariant> =>
    produce(fonts || [], (fontsDraft) => {
      // Save updated fonts list to DOLAN
      usedFontVariants.forEach(({ fontFamily, fontStyle, fontWeight }) => {
        const existingFontVariant = findFont(fontsDraft, fontFamily, fontWeight, fontStyle);
        if (!existingFontVariant) {
          const defaultFont = findFont(defaultFonts, fontFamily, fontWeight, fontStyle);
          if (defaultFont) {
            fontsDraft.push({ ...defaultFont, automatic: true } as AutomaticFontVariant);
          }
        }
      });
      // Remove all fonts from DOLAN that are no longer used, and were added automatically previously
      remove(fontsDraft, (font) => {
        const fontFamily = font.font_family;
        const fontWeight = font.weight;
        const fontStyle = font.style;
        // Remove if this is a broken font definition (due to some old bugs)
        if (font.automatic && !fontFamily) {
          return true;
        }
        if (!font.automatic || defaultFontFamilies.indexOf(fontFamily) < 0) {
          // Never remove custom fonts or manually enabled fonts
          return false;
        }
        // Remove if not found in used font variants
        return !usedFontVariants.some(
          (usedVariant) =>
            usedVariant.fontFamily === fontFamily &&
            usedVariant.fontWeight === fontWeight &&
            usedVariant.fontStyle === fontStyle,
        );
      });
    }),
);

export default getAllFontsInDolan;
